import { HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsModule } from '@ngxs/store';
import { TRACKING_CONFIG } from '@siemens/tracking-suite-angular-lib';
import { provideToastr } from 'ngx-toastr';
import { ExtendedFaqCategoryFilterPipe } from './app/admin/cms-faqs/extended-faq-filter-pipe.pipe';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { provideInterceptors, provideMsalConfiguration, provideMsalServices } from './app/auth/providers';
import { AppState } from './app/auth/store';
import { initializeMsal } from './app/auth/utils';
import { FaqState } from './app/faq/state/faq.state';
import { HelpTopicsState } from './app/help-topics/state/help-topics.state';
import { CriticalInfoState } from './app/home/states/critical-info-state/critical-info.state';
import { SearchState } from './app/search/state/search.state';
import { environment } from './environments/environment';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, environment.languageAssetsPath, '.json');
}

if (environment.production) {
  enableProdMode();
}

const {
  tracking: { apiKey, apiKeySE, apiKeySHS, endpointStage }
} = environment;

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    importProvidersFrom(
      NgxsModule.forRoot([...AppState, CriticalInfoState, SearchState, FaqState, HelpTopicsState], {
        developmentMode: !environment.production,
        compatibility: {
          strictContentSecurityPolicy: true
        }
      })
    ),
    provideTranslateService({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    ExtendedFaqCategoryFilterPipe,
    {
      provide: TRACKING_CONFIG,
      useValue: {
        apiKey: (() => {
          const origin: string = window.location.origin;
          if (origin.includes('energy')) {
            return apiKeySE;
          } else if (origin.includes('healthineers')) {
            return apiKeySHS;
          } else {
            return apiKey;
          }
        })(),
        apiVersion: 'v3',
        endpointStage
      }
    },
    provideAnimations(),
    provideToastr(),
    provideMsalConfiguration({ msalConfig: environment.msalConfig, apiConfig: environment.apiConfig }),
    provideMsalServices(),
    provideInterceptors(),

    provideAppInitializer(() => {
      const initializerFn = initializeMsal(inject(MsalService));
      return initializerFn();
    })
  ]
}).catch(err => console.error(err));
